// Dónde se dibuja cada bus. Lo mandan las paradas: si la de delante marca 0-1 min, el bus va
// hacia ella; mientras la siguiente también marque 0-1 min sigue, parada a parada, y se para en
// la última. Ahí espera quieto hasta que la siguiente marque 0-1 min. Nunca retrocede. Sin
// lecturas, manda el motor (engine.ts).
//
// Lo calcula el servidor una sola vez para todos (así todos los dispositivos ven lo mismo) y la
// app solo lo pinta. La app lo calcula ella misma si no tiene servidor (pruebas, desarrollo).

import type { LineId } from "../data";
import { haversine, type LatLng } from "../geo";
import type { ApiEstimation } from "./api";
import { normDest } from "./infer";
import { pointAt } from "./geometry";
import { BLEND_MS, EVIDENCE_STALE_MS, type Engine, type Tracked } from "./engine";

const READING_FRESH_MS = 90_000; // lecturas más viejas no cuentan
const CHAIN_MIN = 1; // 0-1 min: va hacia ella sin pararse en la anterior
const CHAIN_START_MAX_M = 700; // más lejos, esa parada es de otro bus que no tenemos
const PENDING_BEHIND_M = 600; // una parada con 0-1 min hasta aquí detrás aún no se ha pasado
const CHASE_MPS = 22; // ritmo de la transición al cambiar de trayecto
// Ritmo del bus hacia su parada: lo que le falta en el tiempo que dice la parada (30 s con «0»,
// 60 s con «1 min»), entre MIN y MAX: va como un bus, frenando al llegar, sin correr.
const ARRIVE_S = [30, 60];
const MIN_MPS = 3;
const MAX_MPS = 12;
const AT_STOP_M = 10;

export interface ShownBus {
  id: string;
  line: LineId;
  routeKey: string;
  d: number; // metros del trayecto donde se pinta
  mps: number; // ritmo al que avanza ahora…
  cap: number; // …sin pasar de aquí
  ll: LatLng;
  bearing: number;
  // Mientras se desliza al trayecto nuevo (media vuelta): se pinta en `ll`, no en `d`.
  blending: boolean;
  atStopId: number | null; // parada en la que está latiendo («0 min»)
}

// Estado que se guarda para retomarlo tras un reinicio.
export type ShownSaved = Record<string, { routeKey: string; d: number }>;

export function createShown(engine: Engine) {
  type Blend = NonNullable<Tracked["blendFrom"]>;
  const shownD = new Map<
    string,
    { routeKey: string; d: number; at: number; blend: Blend | null }
  >();
  // Último punto pintado de cada bus: al cambiar de trayecto se desliza desde ahí.
  const lastShown = new Map<string, LatLng>();

  const update = (
    tracked: readonly Tracked[],
    etas: ReadonlyMap<number, readonly ApiEstimation[]>,
    active: ReadonlySet<LineId>,
    now: number,
  ): ShownBus[] => {
    // Minutos del próximo bus de su línea y sentido en una parada (lectura reciente) o null.
    const minAt = (t: Tracked, stopId: number): number | null => {
      const exclude = engine.line(t.bus.line).exclude.get(t.entry.route.key);
      let best: number | null = null;
      for (const e of etas.get(stopId) ?? []) {
        if (e.line !== t.bus.line || (exclude?.has(normDest(e.destination)) ?? false)) continue;
        if (now - (e.observedAt ?? 0) > READING_FRESH_MS) continue;
        if (best === null || e.minutes < best) best = e.minutes;
      }
      return best;
    };
    // Parada donde tiene que estar o hacia la que va el bus. Se parte de la primera que aún no
    // ha pasado: desde un poco por detrás de `d`, la primera que sigue marcando 0-1 min para él
    // (una que ya marca más minutos detrás de él es que ya la pasó). Mientras la siguiente
    // también marque 0-1 min, sigue a la siguiente: se para en la última antes de una con más
    // de 1 min. No la rebasa hasta que su lectura cambie. Null si no hay ninguna cerca.
    const pendingStop = (t: Tracked, d: number): { dist: number; min: number } | null => {
      const stops = t.entry.route.stops;
      let i = -1;
      for (let k = 0; k < stops.length; k++) {
        const s = stops[k]!;
        if (s.dist <= d - PENDING_BEHIND_M) continue;
        if (s.dist - d > CHAIN_START_MAX_M) break;
        const m = minAt(t, s.id);
        if (m === null) continue;
        if (m <= CHAIN_MIN) {
          if (i < 0) i = k;
        } else if (s.dist <= d + 1) i = -1;
        else break;
      }
      if (i < 0) return null;
      let min = minAt(t, stops[i]!.id)!;
      while (i + 1 < stops.length) {
        const m = minAt(t, stops[i + 1]!.id);
        if (m === null || m > CHAIN_MIN) break;
        i++;
        min = m;
      }
      return { dist: stops[i]!.dist, min };
    };
    // ¿Hay lectura de la siguiente parada? Si la hay y no marca 0-1 min, el bus espera quieto.
    const nextHasReading = (t: Tracked, d: number): boolean => {
      const next = t.entry.route.stops.find((s) => s.dist > d + 1);
      return !!next && minAt(t, next.id) !== null;
    };
    const blendFromShown = (t: Tracked): void => {
      const from = lastShown.get(t.id);
      t.blendFrom = null;
      if (!from) return;
      const to = engine.currentLL(t, now)?.ll;
      const m = to ? haversine(from, to) : 0;
      if (m > 3)
        t.blendFrom = { ll: from, at: now, ms: Math.max(BLEND_MS, (m / CHASE_MPS) * 1000) };
    };

    const out: ShownBus[] = [];
    const seen = new Set<string>();
    for (const t of tracked) {
      if (!active.has(t.bus.line) || !t.shown) continue;
      if (now - (t.lastEvidenceAt ?? t.polledAt) > EVIDENCE_STALE_MS) continue;
      let st = shownD.get(t.id);
      // Bus nuevo o cambio de trayecto: se parte de donde lo pone el motor. Si se pintaba en
      // otro sitio (iba por delante del motor), se desliza desde ahí sin saltar.
      const fresh = !st || st.routeKey !== t.entry.route.key;
      // En el mismo trayecto no se usan las transiciones del motor: llevarían el bus hacia
      // atrás cuando aquí va por delante. Solo la propia al cambiar de trayecto, mientras dure.
      if (fresh) blendFromShown(t);
      else t.blendFrom = st!.blend && now < st!.blend.at + st!.blend.ms ? st!.blend : null;
      const engineLL = engine.currentLL(t, now);
      if (!engineLL) continue;
      seen.add(t.id);
      const engineD = engine.currentD(t, now);
      if (!st || fresh) {
        st = { routeKey: t.entry.route.key, d: engineD, at: now, blend: t.blendFrom };
        shownD.set(t.id, st);
      }
      // Mientras se desliza al trayecto nuevo, manda esa transición.
      const blending = !!st.blend && now < st.blend.at + st.blend.ms;
      let mps = 0;
      let cap = st.d;
      if (blending) st.d = engineD;
      else {
        // Hasta la parada pendiente y ni un metro más, aunque el motor vaya por delante.
        const stop = pendingStop(t, st.d);
        // Sin ninguna: espera quieto hasta que la siguiente marque 0-1 min. Sin lectura, el motor.
        const want = stop?.dist ?? (nextHasReading(t, st.d) ? st.d : engineD);
        const left = want - st.d;
        mps = Math.max(MIN_MPS, Math.min(MAX_MPS, left / (ARRIVE_S[stop?.min ?? 1] ?? 60)));
        if (left > 0) {
          st.d = Math.min(want, st.d + (mps * (now - st.at)) / 1000);
          cap = want;
        } else mps = 0;
      }
      st.at = now;
      const p = blending ? engineLL : (pointAt(t.entry.geom, st.d) ?? engineLL);
      const here = t.entry.route.stops.find((s) => Math.abs(s.dist - st.d) <= AT_STOP_M);
      const atStopId = here && minAt(t, here.id) === 0 ? here.id : null;
      t.lastLL = p.ll;
      lastShown.set(t.id, p.ll);
      out.push({
        id: t.id,
        line: t.bus.line,
        routeKey: t.entry.route.key,
        d: st.d,
        mps: st.d < cap ? mps : 0,
        cap: Math.max(cap, st.d),
        ll: p.ll,
        bearing: p.bearing,
        blending,
        atStopId,
      });
    }
    for (const id of shownD.keys()) if (!seen.has(id)) shownD.delete(id);
    for (const id of lastShown.keys()) if (!seen.has(id)) lastShown.delete(id);
    return out;
  };

  return {
    update,
    save(): ShownSaved {
      return Object.fromEntries(
        [...shownD].map(([id, s]) => [id, { routeKey: s.routeKey, d: s.d }]),
      );
    },
    // Tras un reinicio el bus sigue donde se pintaba, no donde lo pone el motor.
    restore(saved: ShownSaved, now: number): void {
      for (const [id, s] of Object.entries(saved))
        shownD.set(id, { routeKey: s.routeKey, d: s.d, at: now, blend: null });
    },
  };
}

export type Shown = ReturnType<typeof createShown>;
